/**
 * AURA Intake Wizard V2 - Main Component
 * Modular 6-step questionnaire for regulatory compliance onboarding
 * 
 * Steps:
 * 1. Company Registration
 * 2. Compliance Information
 * 3. Business Profile
 * 4. Data & Privacy
 * 5. Existing Regulatory Universe
 * 6. Beneficial Ownership & Directors
 * 
 * Features:
 * - Question numbers per step
 * - Page numbers (Step X of Y)
 * - Auto-save with visual indicator
 * - Progress saved to Company Overview
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Check, FloppyDisk, CheckCircle,
  Buildings, ShieldCheck, Briefcase, Database, Globe, Users, Info, CloudCheck
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';

// Import modular step components
import {
  Step1CompanyRegistration,
  Step2ComplianceInformation,
  Step3BusinessProfile,
  Step4DataPrivacy,
  Step5RegulatoryUniverse,
  Step6BeneficialOwnership,
  StepIndicator,
} from '@/components/intake-wizard';

// Step definitions with question counts
const STEPS = [
  { id: 1, title: 'Company Registration', icon: Buildings, questionCount: 8 },
  { id: 2, title: 'Compliance Information', icon: ShieldCheck, questionCount: 12 },
  { id: 3, title: 'Business Profile', icon: Briefcase, questionCount: 4 },
  { id: 4, title: 'Data & Privacy', icon: Database, questionCount: 3 },
  { id: 5, title: 'Existing Regulatory Universe', icon: Globe, questionCount: 2 },
  { id: 6, title: 'Beneficial Ownership', icon: Users, questionCount: 3 },
];

// Initial form state
const getInitialFormData = () => ({
  // Step 1: Company Registration
  registered_company_name: '',
  registration_number: '',
  country_of_incorporation: '',
  financial_year_end: '',
  parent_company_name: '',
  parent_company_registration: '',
  comply_with_parent_regulations: false,
  parent_compliance_rationale: '',
  
  // Step 2: Compliance Information
  compliance_function_internal: false,
  compliance_function_external: false,
  compliance_function_cosourced: false,
  external_compliance_entity_name: '',
  external_compliance_entity_reg: '',
  primary_regulators: [],
  fsp_number: '',
  fsp_registration_date: '',
  fsp_categories: [],
  fic_org_id: '',
  fic_registration_date: '',
  fic_schedules: [],
  fais_compliance_officers: [],
  key_individuals: [],
  mlros: [],
  
  // Step 3: Business Profile
  industries: [],
  aura_activity_categories: [],
  business_activities: [],
  other_activity_description: '',
  
  // Step 4: Data & Privacy
  data_privacy_role: '',
  has_cross_border_transfers: false,
  cross_border_countries: [],
  
  // Step 5: Existing Regulatory Universe
  has_existing_universe: false,
  continue_existing_universe: false,
  
  // Step 6: Beneficial Ownership
  beneficial_owners: [],
  executive_directors: [],
  non_executive_directors: [],
  navi_analysis_status: null,
  navi_analysis_approved: null,
});

// Auto-save debounce delay (ms)
const AUTO_SAVE_DELAY = 2000;

const IntakeWizardV2 = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  
  // State
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [formData, setFormData] = useState(getInitialFormData());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [questionnaireId, setQuestionnaireId] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // idle, saving, saved
  
  // Auto-save timer ref
  const autoSaveTimerRef = useRef(null);
  
  // Load existing questionnaire on mount
  useEffect(() => {
    const loadQuestionnaire = async () => {
      try {
        // First try to create/get questionnaire
        const createRes = await axios.post(
          `${API}/intake-wizard/v2/questionnaire`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setQuestionnaireId(createRes.data.id);
        
        // If existing, load full data
        if (createRes.data.message === 'Existing questionnaire found') {
          const getRes = await axios.get(
            `${API}/intake-wizard/v2/questionnaire`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          setFormData(prev => ({ ...prev, ...getRes.data }));
          setCurrentStep(Math.max(0, (getRes.data.current_step || 1) - 1));
          setCompletedSteps(getRes.data.completed_steps || []);
        }
      } catch (error) {
        console.error('Failed to load questionnaire:', error);
        toast.error('Failed to load questionnaire');
      }
    };
    
    if (token) {
      loadQuestionnaire();
    }
  }, [token]);
  
  // Update a single field with auto-save trigger
  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Trigger auto-save
    setAutoSaveStatus('pending');
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    autoSaveTimerRef.current = setTimeout(() => {
      triggerAutoSave();
    }, AUTO_SAVE_DELAY);
  }, []);
  
  // Auto-save function
  const triggerAutoSave = async () => {
    if (!token) return;
    
    setAutoSaveStatus('saving');
    try {
      await axios.patch(
        `${API}/intake-wizard/v2/questionnaire`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLastSaved(new Date());
      setAutoSaveStatus('saved');
      // Reset to idle after 3 seconds
      setTimeout(() => setAutoSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Auto-save error:', error);
      setAutoSaveStatus('idle');
    }
  };
  
  // Save progress
  const handleSave = async () => {
    setSaving(true);
    setAutoSaveStatus('saving');
    try {
      await axios.patch(
        `${API}/intake-wizard/v2/questionnaire`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLastSaved(new Date());
      setAutoSaveStatus('saved');
      toast.success('Progress saved');
      setTimeout(() => setAutoSaveStatus('idle'), 3000);
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save progress');
      setAutoSaveStatus('idle');
    } finally {
      setSaving(false);
    }
  };
  
  // Calculate total questions up to current step
  const getTotalQuestionsBefore = (stepIndex) => {
    return STEPS.slice(0, stepIndex).reduce((sum, step) => sum + step.questionCount, 0);
  };
  
  const getCurrentStepStartQuestion = () => getTotalQuestionsBefore(currentStep) + 1;
  const getTotalQuestions = () => STEPS.reduce((sum, step) => sum + step.questionCount, 0);
  
  // Navigate to next step
  const handleNext = async () => {
    // Save current step
    await handleSave();
    
    // Mark step as completed
    const stepNum = currentStep + 1;
    if (!completedSteps.includes(stepNum)) {
      setCompletedSteps(prev => [...prev, stepNum].sort((a, b) => a - b));
    }
    
    // Move to next step
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };
  
  // Navigate to previous step
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };
  
  // Check if can proceed to next step
  const canProceed = () => {
    switch (currentStep) {
      case 0: // Company Registration
        return !!formData.registered_company_name;
      case 1: // Compliance Information
        return formData.primary_regulators?.length > 0;
      case 2: // Business Profile
        return formData.aura_activity_categories?.length > 0 || formData.business_activities?.length > 0;
      case 3: // Data & Privacy
        return !!formData.data_privacy_role;
      case 4: // Regulatory Universe
        return true;
      case 5: // Beneficial Ownership
        return true;
      default:
        return true;
    }
  };
  
  // Submit questionnaire and generate regulatory universe
  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Save final data
      await handleSave();
      
      // Submit questionnaire
      await axios.post(
        `${API}/intake-wizard/v2/questionnaire/submit`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Generate regulatory universe
      const universeRes = await axios.post(
        `${API}/intake-wizard/v2/questionnaire/generate-universe`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Questionnaire submitted! Regulatory universe generated.');
      
      // Navigate to universe review page (or dashboard)
      navigate('/regulatory-universe', { 
        state: { universe: universeRes.data } 
      });
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit questionnaire');
    } finally {
      setLoading(false);
    }
  };
  
  // Render current step content with question numbering context
  const renderStepContent = () => {
    const startQ = getCurrentStepStartQuestion();
    const stepProps = { 
      formData, 
      updateField, 
      questionStartNumber: startQ 
    };
    
    switch (currentStep) {
      case 0:
        return <Step1CompanyRegistration {...stepProps} />;
      case 1:
        return <Step2ComplianceInformation {...stepProps} />;
      case 2:
        return <Step3BusinessProfile {...stepProps} />;
      case 3:
        return <Step4DataPrivacy {...stepProps} />;
      case 4:
        return <Step5RegulatoryUniverse {...stepProps} />;
      case 5:
        return <Step6BeneficialOwnership {...stepProps} token={token} />;
      default:
        return null;
    }
  };
  
  // Format last saved time
  const formatLastSaved = () => {
    if (!lastSaved) return null;
    const now = new Date();
    const diff = Math.floor((now - lastSaved) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  return (
    <div className="min-h-screen bg-background" data-testid="intake-wizard-v2">
      {/* Header */}
      <div className="bg-background-surface border-b border-border sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-background rounded-lg transition-colors"
                data-testid="wizard-back-nav-btn"
              >
                <ArrowLeft size={20} className="text-text-secondary" />
              </button>
              <div>
                <h1 className="text-lg font-semibold text-text-primary">AURA Intake Wizard</h1>
                <p className="text-sm text-text-secondary">
                  Page {currentStep + 1} of {STEPS.length} — {STEPS[currentStep].title}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Auto-save status indicator */}
              <div className="flex items-center gap-2 text-sm">
                {autoSaveStatus === 'saving' && (
                  <span className="flex items-center gap-1.5 text-amber-500">
                    <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                    Saving...
                  </span>
                )}
                {autoSaveStatus === 'saved' && (
                  <span className="flex items-center gap-1.5 text-emerald-500">
                    <CloudCheck size={16} weight="fill" />
                    Saved
                  </span>
                )}
                {autoSaveStatus === 'idle' && lastSaved && (
                  <span className="text-text-tertiary">
                    Last saved {formatLastSaved()}
                  </span>
                )}
              </div>
              
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-background border border-border rounded-lg text-text-secondary hover:text-text-primary hover:border-primary transition-colors disabled:opacity-50"
                data-testid="wizard-save-progress-btn"
              >
                <FloppyDisk size={16} />
                {saving ? 'Saving...' : 'Save Progress'}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Company Overview Note */}
      <div className="max-w-4xl mx-auto px-4 pt-4">
        <div className="flex items-start gap-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <Info size={18} className="text-blue-400 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-blue-300">
            <span className="font-medium">Note:</span> Your answers are automatically saved and will appear in the{' '}
            <span 
              onClick={() => navigate('/company-overview')}
              className="text-blue-400 hover:text-blue-300 underline cursor-pointer"
            >
              Company Overview
            </span>{' '}
            section under your profile.
          </p>
        </div>
      </div>
      
      {/* Step Indicator */}
      <div className="max-w-4xl mx-auto px-4 pt-6">
        <StepIndicator 
          steps={STEPS} 
          currentStep={currentStep} 
          completedSteps={completedSteps} 
        />
      </div>
      
      {/* Form Content */}
      <div className="max-w-4xl mx-auto px-4 pb-32" data-testid={`wizard-step-${currentStep + 1}-content`}>
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
        >
          {renderStepContent()}
        </motion.div>
      </div>
      
      {/* Navigation Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-background-surface border-t border-border">
        <div className="max-w-4xl mx-auto px-4 py-4">
          {/* Progress bar */}
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs text-text-tertiary mb-1">
              <span>Questions {getCurrentStepStartQuestion()} - {getCurrentStepStartQuestion() + STEPS[currentStep].questionCount - 1}</span>
              <span>{Math.round(((currentStep + 1) / STEPS.length) * 100)}% Complete</span>
            </div>
            <div className="h-1 bg-border rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${((currentStep + 1) / STEPS.length) * 100}%` }}
              />
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <button
              onClick={handleBack}
              disabled={currentStep === 0}
              className="flex items-center gap-2 px-4 py-2.5 bg-background border border-border rounded-lg text-text-secondary hover:text-text-primary hover:border-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="wizard-back-btn"
            >
              <ArrowLeft size={18} />
              Back
            </button>
            
            <div className="text-sm text-text-tertiary">
              {saving && 'Saving...'}
            </div>
            
            {currentStep < STEPS.length - 1 ? (
              <button
                onClick={handleNext}
                disabled={!canProceed()}
                className="flex items-center gap-2 px-6 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="wizard-continue-btn"
              >
                Continue
                <ArrowRight size={18} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2.5 bg-emerald-500 text-white rounded-lg font-medium hover:bg-emerald-600 transition-colors disabled:opacity-50"
                data-testid="wizard-submit-btn"
              >
                {loading ? 'Submitting...' : 'Submit & Generate Universe'}
                <CheckCircle size={18} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntakeWizardV2;
